import { type FC, useEffect, useRef } from 'react'

import { Box, InfiniteScroll, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0TagPerson } from '@factorialco/f0-react'
import { ReadPostsDynamicTeamPreviewDocument } from 'generated/resources/graphql'

import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'

import 'modules/meetings/lib/utils/index.styles.css'
import { isSpecificationEmpty } from 'modules/posts/components/DynamicTeams/SpecificationSelector'

interface Props {
  specification: string | null
  onError?: (hasError: boolean) => void
}

const DEFAULT_PAGE_SIZE = 25

export const EmployeePreviewPanel: FC<Props> = ({ specification, onError }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const shouldSkipQuery = isSpecificationEmpty(specification)

  const { nodes, controls, loading, data, error } = usePaginatedQuery(
    ReadPostsDynamicTeamPreviewDocument,
    {
      variables: {
        specification: specification ?? '',
        first: DEFAULT_PAGE_SIZE,
      },
      skip: shouldSkipQuery,
      fetchPolicy: 'cache-first',
      extractConnection: (queryData) => ({
        pageInfo: queryData.posts.dynamicTeamPreviewsConnection.pageInfo,
        edges: queryData.posts.dynamicTeamPreviewsConnection.edges,
      }),
    }
  )

  const totalCount = data?.posts.dynamicTeamPreviewsConnection.totalCount ?? 0

  // Notify parent of error state
  useEffect(() => {
    if (onError) {
      onError(!!error)
    }
  }, [error, onError])

  const getDisplayName = (employee: {
    preferredName?: string | null
    firstName: string
    lastName: string
  }): string => {
    if (employee.preferredName) {
      return employee.preferredName
    }
    return `${employee.firstName} ${employee.lastName}`
  }

  if (shouldSkipQuery) {
    return (
      <Box
        flexDirection="column"
        gap="s8"
        padding="s16"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Text.Normal color="grey600" size="100">
          {i18n.t('posts.communities.automatic_invites.preview.empty_specification')}
        </Text.Normal>
      </Box>
    )
  }

  if (loading && nodes.length === 0) {
    return (
      <Box
        flexDirection="column"
        gap="s8"
        padding="s16"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        flexDirection="column"
        gap="s8"
        padding="s16"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
      >
        <Text.Normal color="grey600" size="100">
          {i18n.t('posts.communities.automatic_invites.preview.evaluation_error')}
        </Text.Normal>
      </Box>
    )
  }

  if (nodes.length === 0) {
    return (
      <Box flexDirection="column" gap="s8" padding="s16">
        <Text.Normal color="grey600" size="100">
          {i18n.t('posts.communities.automatic_invites.preview.no_employees')}
        </Text.Normal>
      </Box>
    )
  }

  return (
    <Box flexDirection="column" gap="s8">
      <Box paddingBottom="s8">
        <Text.Normal size="100" color="grey600">
          {i18n.tp('posts.communities.automatic_invites.preview.title', {
            count: totalCount,
          })}
        </Text.Normal>
      </Box>
      <Box
        ref={scrollContainerRef}
        flexDirection="column"
        gap="s8"
        style={{ maxHeight: '360px' }}
        className={'utils_hiddenScrollBar'}
      >
        <InfiniteScroll
          threshold={100}
          loadMore={controls.fetchMore}
          hasMore={controls.hasMore}
          scrollContainerRef={scrollContainerRef}
          loader={
            <Box key="loader" padding="s16" alignItems="center" justifyContent="center">
              <Spinner />
            </Box>
          }
        >
          {nodes.map((employee) => (
            <Box key={employee.id} paddingBottom="s4">
              <F0TagPerson name={getDisplayName(employee)} src={employee.avatarUrl ?? undefined} />
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </Box>
  )
}
